import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isProfileComplete } from '../../lib/rolePermissions';
import SEO from '../common/SEO';
import EmailVerificationModal from './EmailVerificationModal';

// Authoritative login assets per master reference 01.webp
import upperBookmarkLogo from '../../assets/login/upper-bookmark-logo.webp';
import sheetUpElement from '../../assets/login/login-sheet-up-element.webp';
import sheetDownElement from '../../assets/login/login-sheet-down-element.webp';

import '../../styles/LoginPage.css';

const GoogleIcon = () => (
  <svg className="sx-google-svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');

  const { user, profile, roles, signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect');

  let from = '/';
  if (redirectParam) {
    from = redirectParam;
  } else if (location.state?.from) {
    if (typeof location.state.from === 'string') {
      from = location.state.from;
    } else if (location.state.from.pathname) {
      from = location.state.from.pathname + (location.state.from.search || '');
    }
  }

  // Dismiss privacy consent banner on dedicated login screen
  useEffect(() => {
    try {
      if (!localStorage.getItem('senritsux_analytics_consent')) {
        localStorage.setItem('senritsux_analytics_consent', 'accepted');
      }
    } catch {}
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      if (profile && !isProfileComplete(profile, roles)) {
        const destQuery = from && from !== '/' && from !== '/dashboard' ? `&redirect=${encodeURIComponent(from)}` : '';
        navigate(`/edit-profile?firstTime=true${destQuery}`, { replace: true, state: { from } });
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [user, profile, roles, navigate, from]);

  const validateEmail = (val) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!validateEmail(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setLoading(true);
    try {
      const data = await signIn(email.trim(), password);
      if (data?.user) {
        if (!data.user.email_confirmed_at && !data.user.confirmed_at) {
          setUnverifiedEmail(data.user.email || email.trim());
          setShowVerifyModal(true);
          setLoading(false);
          return;
        }
        if (profile && !isProfileComplete(profile, roles)) {
          const destQuery = from && from !== '/' && from !== '/dashboard' ? `&redirect=${encodeURIComponent(from)}` : '';
          navigate(`/edit-profile?firstTime=true${destQuery}`, { replace: true, state: { from } });
        } else {
          navigate(from, { replace: true });
        }
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      const errMsg = err?.message || '';
      if (
        errMsg.toLowerCase().includes('verify your email') ||
        errMsg.toLowerCase().includes('email not confirmed')
      ) {
        setUnverifiedEmail(email.trim());
        setShowVerifyModal(true);
      } else {
        setError(errMsg || 'Invalid email or password.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setOauthLoading(true);
    try {
      if (from && from !== '/') {
        try {
          sessionStorage.setItem('auth_redirect_destination', from);
        } catch {}
      }
      try {
        sessionStorage.setItem('google_oauth_intent', 'login');
      } catch {}
      await signInWithGoogle();
    } catch (err) {
      setError(err.message || 'Google sign-in failed. Please try again.');
      setOauthLoading(false);
    }
  };

  return (
    <div className="sx-login-root">
      <SEO
        title="Artist Login | SenritsuX"
        description="Log in to your SenritsuX artist account to manage music distribution, track releases, and collect royalties."
        canonical="/login"
        noindex={true}
      />

      <main className="sx-login-container">
        {/* LEFT SECTION: Upper bookmark-style logo and slogan panel */}
        <section className="sx-login-branding-panel" aria-label="SenritsuX Branding">
          <div className="sx-branding-bookmark-wrapper">
            <img
              src={upperBookmarkLogo}
              alt="SenritsuX - Your Gateway To Be An Artist"
              className="sx-branding-bookmark-img"
              loading="eager"
            />
          </div>
        </section>

        {/* RIGHT SECTION: Dedicated Relative-Positioned Login Sheet Container */}
        <section className="sx-login-sheet-container" aria-label="Login Form Sheet">
          {/* Decorative Illustration 1: Upper CD Disc with Sparkle Star */}
          <div className="sx-sheet-decor-up" aria-hidden="true">
            <img
              src={sheetUpElement}
              alt=""
              className="sx-sheet-decor-up-img"
            />
          </div>

          {/* Decorative Illustration 2: Lower Halftone Vinyl Record with Hands */}
          <div className="sx-sheet-decor-down" aria-hidden="true">
            <img
              src={sheetDownElement}
              alt=""
              className="sx-sheet-decor-down-img"
            />
          </div>

          {/* Main Login Card */}
          <div className="sx-login-card">
            <header className="sx-login-card-header">
              <h1 className="sx-login-title">Welcome Back</h1>
              <p className="sx-login-subtitle">
                Sign in to your <span className="sx-brand-name">SenritsuX</span> account
              </p>
            </header>

            {/* Error Message Toast */}
            {error && (
              <div className="sx-login-error-banner" role="alert">
                <AlertTriangle size={15} style={{ flexShrink: 0 }} />
                <span>{error}</span>
              </div>
            )}

            {/* Google OAuth Button */}
            <button
              type="button"
              className={`sx-login-btn-google ${oauthLoading ? 'loading' : ''}`}
              onClick={handleGoogleLogin}
              disabled={oauthLoading || loading}
              aria-label="Continue with Google (Recommended)"
            >
              {oauthLoading ? (
                <span className="auth-spinner auth-spinner-light" style={{ width: 18, height: 18 }} />
              ) : (
                <>
                  <span className="sx-google-btn-left">
                    <GoogleIcon />
                    <span className="sx-google-btn-text">Continue with Google</span>
                  </span>
                  <span className="sx-login-recommended-badge">RECOMMENDED</span>
                </>
              )}
            </button>

            {/* Divider */}
            <div className="sx-login-divider">
              <span className="sx-divider-line" />
              <span className="sx-divider-text">OR CONTINUE WITH EMAIL</span>
              <span className="sx-divider-line" />
            </div>

            {/* Email & Password Form */}
            <form onSubmit={handleLogin} noValidate className="sx-login-form">
              <div className="sx-form-group">
                <label htmlFor="login-email" className="sx-form-label">Email</label>
                <input
                  id="login-email"
                  type="email"
                  className={`sx-form-input ${error && !email.trim() ? 'input-error' : ''}`}
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                  aria-label="Email address"
                />
              </div>

              <div className="sx-form-group">
                <label htmlFor="login-password" className="sx-form-label">Password</label>
                <div className="sx-password-input-wrapper">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    className={`sx-form-input sx-password-input ${error && !password ? 'input-error' : ''}`}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="current-password"
                    aria-label="Password"
                  />
                  <button
                    type="button"
                    className="sx-password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={0}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="sx-forgot-wrapper">
                <Link to="/forgot-password" className="sx-forgot-link">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                className={`sx-login-submit-btn ${loading ? 'loading' : ''}`}
                disabled={loading}
                aria-label="Login"
              >
                {loading ? (
                  <span className="auth-spinner" style={{ width: 18, height: 18 }} />
                ) : (
                  'Login'
                )}
              </button>
            </form>

            {/* Card Footer */}
            <footer className="sx-login-card-footer">
              <span>Don't have an account?{' '}</span>
              <Link
                to={redirectParam ? `/signup?redirect=${encodeURIComponent(redirectParam)}` : '/signup'}
                className="sx-signup-link"
              >
                Sign Up
              </Link>
            </footer>
          </div>
        </section>
      </main>

      {/* Email Verification Modal */}
      {showVerifyModal && (
        <EmailVerificationModal
          isOpen={showVerifyModal}
          email={unverifiedEmail || email}
          onClose={(verified) => {
            setShowVerifyModal(false);
            if (verified) {
              navigate(from, { replace: true });
            }
          }}
        />
      )}
    </div>
  );
}
